import React, {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useRef,
} from "react";
import { useAuthContext } from "./AuthContext";
import { authenticateServer } from "../utils/authUtils";
import {
    userPool,
    decodeJWT,
    logoutServer,
    getPreSignedUrl as getPreSignedUrlFromService,
} from "../services/cognitoService";
import { createAuthBroadcaster } from "../utils/authBroadcast";

function useCognito() {
    const { setIsAuthenticated, setUserData } = useAuthContext();
    const { emit: emitAuthEvent, listen: listenAuthEvents } = createAuthBroadcaster();
    const isRefreshingRef = useRef(false);
    const refreshPromiseRef = useRef(null);
    const authInitPromiseRef = useRef(null);
    const hasAuthenticatedRef = useRef(false);

    const handleServerAuthFailure = (user, err, reject) => {
        const isRebooting = localStorage.getItem("sonda.backend.rebooting") === "true";
        if (isRebooting) {
            console.warn("Backend auth failed, but rebooting — skipping logout.");
            return reject(err);
        }
        console.error("Backend auth failed:", err);
        authInitPromiseRef.current = null;
        hasAuthenticatedRef.current = false;
        user?.signOut?.();
        setIsAuthenticated(false);
        reject(err);
    };

    const handleServerAuthSuccess = async (user, token, resolve, reject) => {
        try {
            const alreadyAuthenticated =
                localStorage.getItem("isBackendAuthenticated") === "true";
            const decodedToken = decodeJWT(token);
            let userPayload;
            if (!alreadyAuthenticated) {
                const serverAuthData = await authenticateServer(token);
                userPayload = { sub: decodedToken.sub, ...serverAuthData };
                emitAuthEvent("login", userPayload);
                localStorage.setItem("isBackendAuthenticated", "true");
            } else {
                const storageUser = {
                    email: localStorage.getItem("userEmail") || "",
                    firstName: localStorage.getItem("firstName") || "",
                    lastName: localStorage.getItem("lastName") || "",
                    groups: JSON.parse(localStorage.getItem("groups") || "[]"),
                };
                userPayload = { sub: decodedToken.sub, ...storageUser };
            }
            setUserData(userPayload);
            setIsAuthenticated(true);
            hasAuthenticatedRef.current = true;
            resolve(user);
        } catch (err) {
            hasAuthenticatedRef.current = false;
            handleServerAuthFailure(user, err, reject);
        }
    };

    const getAuthenticatedUser = useCallback(() => {
        if (authInitPromiseRef.current) {
            return authInitPromiseRef.current;
        }
        const user = userPool.getCurrentUser();
        if (!user) {
            setIsAuthenticated(false);
            return Promise.reject(new Error("No authenticated user found"));
		}

        authInitPromiseRef.current = new Promise((resolve, reject) => {
            user.getSession(async (err, session) => {
                if (err) {
                    authInitPromiseRef.current = null;
                    return reject(err);
                }

                const idToken = session.getIdToken().getJwtToken();
                const decodedToken = decodeJWT(idToken);
                const currentTime = Math.floor(Date.now() / 1000);
                const timeLeft = decodedToken.exp - currentTime;

                try {
                    if (timeLeft < 60 || decodedToken.exp < currentTime) {
                        if (!isRefreshingRef.current) {
                            isRefreshingRef.current = true;
                            refreshPromiseRef.current = new Promise((refreshResolve, refreshReject) => {
                                user.refreshSession(session.getRefreshToken(), async (refreshErr, newSession) => {
                                    isRefreshingRef.current = false;
                                    if (refreshErr) {
                                        console.error("Error refreshing session:", refreshErr);
                                        return refreshReject(refreshErr);
                                    }
                                    console.log("Session refreshed successfully.");
                                    const newIdToken = newSession.getIdToken().getJwtToken();
                                    await handleServerAuthSuccess(user, newIdToken, refreshResolve, refreshReject);
                                });
                            });
                        }
                        await refreshPromiseRef.current;
                    } else {
                        await handleServerAuthSuccess(user, idToken, resolve, reject);
                    }
                    resolve(user);
                } catch (err) {
                    reject(err);
                }
            });
        }).finally(() => {
            authInitPromiseRef.current = null;
        });

        return authInitPromiseRef.current;
    }, [userPool, setUserData]);

    useEffect(() => {
        const checkAuthState = async () => {
            const user = userPool.getCurrentUser();
            const storedAuth = localStorage.getItem("isBackendAuthenticated");

            // If no Cognito session or local flag missing - logout
            if (!user || !storedAuth) {
                console.warn("Lost session detected — signing out.");
                signOut();
                setIsAuthenticated(false);
                return;
            }

            try {
                await getAuthenticatedUser();
            } catch (err) {
                const isRebooting = localStorage.getItem("sonda.backend.rebooting") === "true";
                if (isRebooting) {
                    console.error("Authentication check failed:", err);
                    return;
                }
            }
        };
        checkAuthState();

        // Listen for auth events from other tabs
        const unsubscribe = listenAuthEvents(({ type, payload }) => {
            if (type === "login" && payload) {
                setUserData(payload);
                setIsAuthenticated(true);
            } else if (type === "logout") {
                setIsAuthenticated(false);
                setUserData({
                    email: "",
                    groups: [],
                    firstName: "",
                    lastName: "",
                    sub: "",
                });
            }
        });

        // Check on focus
        window.addEventListener("focus", checkAuthState);

        return () => {
            unsubscribe();
            window.removeEventListener("focus", checkAuthState);
        };
    }, [getAuthenticatedUser, setIsAuthenticated, setUserData]);

    const signOut = () => {
        logoutServer();
        [
            "groups",
            "userEmail",
            "firstName",
            "lastName",
            "sonda.backend.rebooting",
            "showUpdateInProgressModal",
        ].forEach((key) => localStorage.removeItem(key));

        localStorage.removeItem("isBackendAuthenticated");
        sessionStorage.removeItem("updateWarningDismissed");

        setIsAuthenticated(false);
        hasAuthenticatedRef.current = false;
        authInitPromiseRef.current = null;
        emitAuthEvent("logout");

        return userPool.getCurrentUser()?.signOut();
    };

    const signOutClient = () => {
        setIsAuthenticated(false);
        hasAuthenticatedRef.current = false;
        emitAuthEvent("logout");
        return userPool.getCurrentUser()?.signOut();
    };

    const getSession = (user) => {
        return new Promise((resolve, reject) => {
            user.getSession((err, session) => {
                if (err) {
                    console.error("Error getting session:", err);
                    return reject(err);
                }
                resolve(session);
            });
        });
    };

    const refreshSession = (user) => {
        return new Promise((resolve, reject) => {
            user.getSession((err, session) => {
                if (err) {
                    console.error("Error refreshing session:", err);
                    return reject(err);
                }

                user.refreshSession(session.getRefreshToken(), (refreshErr, newSession) => {
                    if (refreshErr) {
                        console.error("Error refreshing session:", refreshErr);
                        return reject(refreshErr);
                    }
                    resolve(newSession);
                });
            });
        });
    };

    const getPreSignedUrl = async (url) => {
        const user = await getAuthenticatedUser();
        if (!user) {
            throw new Error("User not authenticated.");
        }

        try {
            const session = await getSession(user);
            if (!session.isValid()) {
                await refreshSession(user);
            }
            const idToken = session.getIdToken().getJwtToken();
             const signedUrl = await getPreSignedUrlFromService(url, idToken);
            return signedUrl;
        } catch (error) {
            console.error("Error generating signed URL:", error);
            throw new Error("User session is not valid.");
        }
    };

    return {
        userPool,
        getAuthenticatedUser,
        signOut,
        signOutClient,
        getPreSignedUrl,
    };
}

const CognitoContext = createContext(null);

export function CognitoProvider({ children }) {
    const cognito = useCognito();
    return <CognitoContext.Provider value={cognito}>{children}</CognitoContext.Provider>;
}

export function useCognitoContext() {
    const context = useContext(CognitoContext);
    if (!context) throw new Error("useCognitoContext must be used within a CognitoProvider");
    return context;
}

export default CognitoContext;